import {
  getLogger,
  postJsMessage,
  registerSpecificNotificationHandler,
} from '../../bridge';
import API from '../../api';
import { isAvailable } from '../utils';
import { NativeAPIResponseMessage } from '../../utils/type';

export enum ShortcutKeyName {
  // Team Chat
  screenshot = 'screenshot',
  search_in_chat_or_channel = 'search_in_chat_or_channel',
  hide_current_chat_session = 'hide_current_chat_session',
  goto_prev_chat = 'goto_prev_chat',
  goto_next_chat = 'goto_next_chat',
  back = 'back',
  forward = 'forward',
  new_chat = 'new_chat',
  collapse_all = 'collapse_all',
  focus_keyboard_to_list_of_all_chats = 'focus_keyboard_to_list_of_all_chats',
  focus_keyboard_to_last_msg_in_chat = 'focus_keyboard_to_last_msg_in_chat',
  add_member_to_channel = 'add_member_to_channel',
  jump_to_start_of_new_msg_in_chat = 'jump_to_start_of_new_msg_in_chat',
  focus_chat_input_field = 'focus_chat_input_field',

  // Clips
  record_new_clip = 'record_new_clip',

  // General
  global_search = 'global_search',
}

export type ShortcutListener = (data: {
  name: ShortcutKeyName;
  isGlobal: boolean;
}) => void;

const hotKeyListeners = new Map<string, Set<ShortcutListener>>();
let isGlobalListenerRegistered = false;

const globalHotKeyHandler = (
  notification: NativeAPIResponseMessage<{
    name: ShortcutKeyName;
    isGlobal: boolean;
  }>
) => {
  const logger = getLogger();
  const { name, isGlobal } = notification.result;
  if (!name) return;
  const listeners = hotKeyListeners.get(name);

  if (listeners && listeners.size > 0) {
    listeners.forEach((listener) => {
      try {
        listener({ name, isGlobal });
      } catch (error) {
        logger.error(`Error in hotkey listener for ${name}:`, error);
      }
    });
  }
};

async function ensureGlobalListenerRegistered() {
  if (!isGlobalListenerRegistered) {
    registerSpecificNotificationHandler(
      'UnifyNotify_OnHotKeyPressed',
      globalHotKeyHandler
    );
    isGlobalListenerRegistered = true;
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.addShortcutListener
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function addShortcutListener(
  name: ShortcutKeyName,
  isGlobal: boolean,
  listener: ShortcutListener
): Promise<boolean> {
  const logger = getLogger();
  try {
    await isAvailable('webview.addShortcutListener');
    await ensureGlobalListenerRegistered();

    const {
      returnCode,
      result,
    }: NativeAPIResponseMessage<{ success: boolean }> = await postJsMessage(
      API.registerShortcutKey,
      {
        name,
        isGlobal,
      }
    );

    if (returnCode || !result.success) {
      logger.warn(
        `addShortcutListener registerShortcutKey failed with returnCode(${returnCode})`,
        result
      );
      return false;
    }

    if (!hotKeyListeners.has(name)) {
      hotKeyListeners.set(name, new Set());
    }

    const listeners = hotKeyListeners.get(name)!;
    listeners.add(listener);

    return true;
  } catch (error) {
    const logger = getLogger();
    logger.error(
      `addShortcutListener ${name}:isGlobal(${isGlobal}) Error`,
      error
    );
    return false;
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * webview.removeShortcutListener
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function removeShortcutListener(
  name: ShortcutKeyName,
  isGlobal: boolean,
  listener: ShortcutListener
): Promise<boolean> {
  const logger = getLogger();
  try {
    await isAvailable('webview.removeShortcutListener');
    const listeners = hotKeyListeners.get(name);

    if (!listeners || !listeners.has(listener)) {
      logger.warn(
        `removeShortcutListener missing listener for ${name}:isGlobal(${isGlobal})`,
        listeners
      );
      return false;
    }

    listeners.delete(listener);

    if (listeners.size === 0) {
      hotKeyListeners.delete(name);

      await isAvailable('webview.removeShortcutListener');
      const {
        returnCode,
        result,
      }: NativeAPIResponseMessage<{ success: boolean }> = await postJsMessage(
        API.unregisterShortcutKey,
        { name, isGlobal }
      );

      if (returnCode || !result.success) {
        logger.warn(
          `removeShortcutListener unregisterShortcutKey failed with returnCode(${returnCode})`,
          result
        );
        return false;
      }
    }

    return true;
  } catch (error) {
    logger.error(`removeShortcutListener ${name}:${isGlobal} Error`, error);
    return false;
  }
}
